<template>
  <div class="container project-charts">
    <h1>Evaluation 2000 watts</h1>

    <div v-if="!graphData || Object.keys(graphData).length === 0" class="empty">
      Pas de données graphiques disponibles.
    </div>

    <div v-else class="sections">
      <section v-for="key in sectionKeys" :key="key" class="chart-section">
        <h2>{{ getSectionName(key) }}</h2>
        <br>
        <!-- Row 1: table (consumption vs limits) + bar chart -->
        <div class="section-grid">
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Rubrique</th>
                  <th>Consommation</th>
                  <th>Limite</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in getSectionRows(key)" :key="row.label">
                  <td class="label-cell">
                    <span class="color-dot" :style="{ backgroundColor: row.color }"></span>
                    {{ row.label }}
                  </td>
                  <td class="value-cell">{{ formatValue(row.consumption, key) }}</td>
                  <td class="limit-cell">{{ formatValue(row.limit, key) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ApexCharts stacked/superposed bar -->
          <div class="chart-wrap">
            <apexchart
              v-if="hasBarData(key)"
              type="bar"
              :options="getBarOptions(key)"
              :series="getBarSeries(key)"
              height="280"
            />
            <div v-else class="chart-placeholder">Données manquantes</div>
          </div>
        </div>

        <!-- Row 2: data list + pie chart -->
        <div class="section-grid">
          <div class="data-list-wrap">
            <ul>
              <li v-for="d in getDataList(key)" :key="d.label" class="consumption-item">
                <span class="color-dot" :style="{ backgroundColor: d.color }"></span>
                <span class="consumption-label">{{ d.label }}</span>
                <span class="consumption-value">{{ formatValue(d.value,key) }}</span>
              </li>
            </ul>
          </div>

          <!-- ApexCharts pie/donut -->
          <div class="chart-wrap">
            <apexchart
              v-if="hasPieData(key)"
              type="donut"
              :options="getPieOptions(key)"
              :series="getPieSeries(key)"
              width="380"
              height="280"
            />
            <div v-else class="chart-placeholder">Données manquantes</div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import VueApexCharts from 'vue3-apexcharts'
import '../../assets/styles/user.css';

export default {
  name: "ProjectCharts",
  components: { apexchart: VueApexCharts },
  data() {
    return {
      sectionKeys: ['ENR', 'EP', 'GES']
    };
  },
  computed: {
    ...mapState("project", ["selectedProject", "projectData", "roles", "graphData"]),
    ...mapGetters("project", ["filterActiveProject"]),
  },
  async mounted() {
    if (this.selectedProject === 0) {
      if (this.$route.params.id) {
        await this.$store.dispatch("project/fetchProjectList")
        await this.checkRouteParams()
        if (this.selectedProject) {
          await this.$store.dispatch("project/fetchGraphData", this.selectedProject)
        }
      }
    } else {
      await this.$store.dispatch("project/fetchGraphData", this.selectedProject)
    }
  },
  methods: {
    async checkRouteParams() {
      const routeId = this.$route.params.id
      if (routeId) {
        try {
          const projectId = parseInt(routeId)
          const project = this.projectData.find(p => p.project_id === projectId)
          if (project) {
            this.$store.commit("project/selectProjectMutation", projectId)
          } else {
            console.error("Project not found in the existing list: ", projectId)
          }
        } catch (error) {
          console.error("Error in checkRouteParams: ", error)
        }
      } else {
        this.$store.commit("project/selectProjectMutation", 0)
      }
    },

    // Helpers to extract sections
    getSection(key) {
      if (!this.graphData || !this.graphData[key]) return { data: [], consumption: [], limits: [] }
      const parts = this.graphData[key] || []
      const findByName = name => (parts.find(p => p.name === name) || { dataSet: [] }).dataSet
      //console.log("Charts ;",this.graphData[key][2].dataSet[3], "\n", this.graphData[key][2].dataSet[4])
      //console.log("charts name :", parts)
      return {
        data: findByName('data'),
        consumption: findByName('consumption'),
        limits: findByName('limits')
      }
    },

    // Build rows by merging consumption and limits by label (consumption placed in table)
    getSectionRows(key) {
      const { consumption, limits } = this.getSection(key)
      const mapLimits = (limits || []).reduce((acc, item) => { acc[item.label] = item; return acc }, {})
      const mapConsumption = (consumption || []).reduce((acc, item) => { acc[item.label] = item; return acc }, {})

      // Use labels from consumption first, then add any limits-only labels
      const labels = [...new Set([...(consumption || []).map(d => d.label), ...(limits || []).map(l => l.label)])]
      
      
      return labels.map(label => {
        const c = mapConsumption[label] || { value: null, color: (mapLimits[label] && mapLimits[label].color) || '#888' }
        const l = mapLimits[label] || { value: null, color: c.color }
        return {
          label,
          consumption: c.value ? c.value : '-',
          limit: l.value? l.value : 0,
          color: c.color || l.color || '#888'
        }
      })
    },

    // Return data list for rendering (was previously "consumption" list)
    getDataList(key) {
      const { data } = this.getSection(key)
      const safeData = [ ...(data || [])]
      /* data.forEach((item, index) => {
        //console.log("Vérification item :", item.label, "à l'index", index) 
        if(item.label == 'Mobilité' || item.label == "Construction"){
          //console.log("return filtre index ", item.label, index)
          indexes.push(index);
        }
      })
      //console.log("indexes",indexes) */
      let indexes = [4,5]
      indexes.sort((a, b) => b - a)
        .forEach(i => {
          safeData.splice(i, 1)
        })
      return (safeData || [])
        .map(c => ({
        label: c.label,
        value: c.value?c.value:'-',
        color: c.color || '#888'
      }))
    },

    formatValue(val, key) {
      let nrVirg = 1;
      if (val === null || val === undefined) return '-'
      if(key == "GES")nrVirg=2;
      if (typeof val === 'number'){
        val = val.toFixed(nrVirg);
      }
      return val
    },

    getSectionName(key) {
      const names = {
        'ENR': 'Énergie non renouvelable',
        'EP': 'Énergie primaire',
        'GES': 'Gaz à Effet de Serre'
      }
      return names[key] || key
    },

    /* ---------- ApexCharts helpers ---------- */

    // Build series for stacked bar: use first 3 consumption entries (or available)
    getBarSeries(key) {
      const { consumption } = this.getSection(key)
      const items = (consumption || []).slice(0, 3)
      return items.map(item => ({
        name: item.label,
        data: [Number(item.value?item.value:0) || 0],
        color: item.color || '#888'
      }))
    },

    // Options for bar: single category, stacked, annotations for 4th and 5th limits
        // Options for bar: single category, stacked, annotations for 4th and 5th limits
    getBarOptions(key) {
      const { limits/* , consumption */ } = this.getSection(key)
      //console.log("consumption test : ",consumption)
      const limit4 = limits && limits[3] ? Number(limits[3].value) : null // C+E
      const limit5 = limits && limits[4] ? Number(limits[4].value) : null // Total
      const series = this.getBarSeries(key)
      const colors = series.map(s => s.color)

      // compute y max based on series data
      const seriesSum = series.reduce((acc, s) => acc + (s.data[0] || 0), 0)
      let yMax = Math.ceil(Math.max(seriesSum * 1.2, 10))
      
      // override if a limit exists and is higher
      if (limit5 && !isNaN(limit5) && limit5 > seriesSum) {
        yMax = Math.ceil(limit5 * 1.1)
      } else if (limit4 && !isNaN(limit4) && limit4 > seriesSum) {
        yMax = Math.ceil(limit4 * 1.1)
      }

      const annotations = { yaxis: [] }
      if (limit4 !== null && !isNaN(limit4)) {
        annotations.yaxis.push({
          y: limit4,
          borderColor: '#FFD700',
          strokeDashArray: 6,
          label: { text: `Limite C+E: ${limit4}`, style: { background: '#FFD700', color: '#000' } }
        })
      }
      if (limit5 !== null && !isNaN(limit5)) {
        annotations.yaxis.push({
          y: limit5,
          borderColor: '#FF0000',
          strokeDashArray: 6,
          label: { text: `Limite Totale: ${limit5}`, style: { background: '#FF0000', color: '#fff' } }
        })
      }

      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: { horizontal: false, columnWidth: '60%' }
        },
        xaxis: {
          categories: [this.getSectionName(key)]
        },
        yaxis: {
          min: 0,
          max: yMax
        },
        colors,
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        tooltip: { y: { formatter: v => (v === null ? '-' : this.formatValue(v, key)) } },
        annotations
      }
    },

    // Has bar data
    hasBarData(key) {
      const s = this.getBarSeries(key)
      /* const cons = this.getSection(key)
      const total = cons.consumption[4] */
      //console.log("hasBarData : Hide n'seek", total.value)
      //console.log("hasBarData ", s.length)
      //console.log("hasBarData2 : ", s.some(x => (x.data[x]) > 1))
      return s /* && s.length > 3 */ && s.some(x => (x.data[0] || 0) > 0) 
    },

    /* Pie series and options (use data dataset with same colors) */
    getPieSeries(key) {
      const { data } = this.getSection(key)
      return (data || []).map(d => Number(d.value?d.value:0) || 0)
    },
    getPieOptions(key) {
      const {data} = this.getSection(key)
      const labels = (data || []).map(d => d.label)
      const colors = (data || []).map(d => d.color || '#888')
      return {
        chart: { type: 'donut' },
        labels,
        colors,
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Math.round(val)}%`,
          style: { colors: ['#fff'] }
        },
        legend: { position: 'bottom' },
        tooltip: { y: { formatter: v =>(v === null ? '-' : this.formatValue(v, key))} },
        plotOptions: {
          pie: {
            donut: { size: '0%' }
          }
        }
      }
    },
    hasPieData(key) {
      const s = this.getPieSeries(key)
      return s && s.length > 0 && s.reduce((a, b) => a + b, 0) > 0
    }
  },
};
</script>

<style scoped>
.project-charts { padding: 16px; }
.chart-section { margin: 20px 0; border: 1px solid #e6e6e6; padding: 12px; border-radius: 8px; background: #fff; }

/* layout: two columns, each max 50% */
.section-grid {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.table-wrap, .data-list-wrap, .chart-wrap {
  flex: 1 1 50%;
  max-width: 50%;
  box-sizing: border-box;
}

/* table tweaks */
.value-cell, .limit-cell { width: 120px; text-align: right !important;}
.data-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
.data-table th, .data-table td { padding: 8px 10px; border-bottom: 1px solid #f0f0f0; text-align: left; }
.label-cell { display:flex; align-items:center; gap:10px; }
.color-dot { width:12px; height:12px; border-radius:50%; display:inline-block; margin-right:6px; }

/* stacked data list (vertical) */
.data-list-wrap ul { list-style:none; padding:0; margin:0; display:block; }
.consumption-item {
  display:flex;
  align-items:center;
  gap:8px;
  padding:8px 10px;
  background:#fafafa;
  border-radius:6px;
  width:100%;
  margin-bottom:8px;
  box-sizing:border-box;
}
.consumption-item:last-child { margin-bottom:0; }
.consumption-label { flex:1; }
.consumption-value { font-weight:600; }
.consumption-item span{
    font-size: .875rem;
}

/* chart placeholder styling */
.chart-placeholder {
  min-height: 220px;
  border-radius: 8px;
  background: linear-gradient(180deg,#fafafa,#f4f6f8);
  border: 1px dashed #ddd;
  display:flex;
  align-items:center;
  justify-content:center;
  color:#666;
  font-weight:600;
  padding:12px;
  text-align:center;
}
.placeholder-inner { padding:8px; }

.chart-wrap {
  display: flex;
  align-items: center;      /* vertical center */
  justify-content: center;  /* horizontal center */
}

/* ensure the actual chart element is centered if it renders as a block */
.chart-wrap > * {
  margin: 0 auto;
}

/* responsive: stack on small screens */
@media (max-width: 900px) {
  .section-grid { flex-direction: column; }
  .table-wrap, .data-list-wrap, .chart-wrap { max-width: 100%; flex-basis: 100%; }
}
.empty { color:#666; padding:8px 0; }
</style>